import React from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { withSuspense } from "~/lib/suspense";
import { TranscriptModalContextProvider } from "~/meetingRecorder/TranscriptModalContext";
import TranscriptPreviewFailedModal from "~/meetings/TranscriptPreviewFailedModal";
import type { ModalProps } from "~/ui/modals";

import MeetingModal, { LoadingMeetingModal } from "./MeetingModal";
import { MeetingModalContextProvider } from "./MeetingModalContext";
import type { MeetingModalCommonProps } from "./openMeetingModal";

import type { MeetingModalThroughRecordingIdQuery } from "~/__generated__/MeetingModalThroughRecordingIdQuery.graphql";

const meetingQuery = graphql`
	query MeetingModalThroughRecordingIdQuery($id: ID!) {
		node(id: $id) {
			__typename
			... on MeetingRecording {
				createdAt
				note {
					id
					...MeetingModal_note
				}

				...MeetingContent_meetingRecording @relay(mask: false)
			}
		}

		viewer {
			...MeetingContent_viewer
		}
	}
`;

function MeetingModalThroughRecordingId({
	close,
	recordingId,
	initialVideoTimestamp,
	timestampStart,
	timestampEnd,
}: ModalProps<unknown> &
	MeetingModalCommonProps & {
		recordingId: string;
	}) {
	const data = useLazyLoadQuery<MeetingModalThroughRecordingIdQuery>(
		meetingQuery,
		{ id: recordingId },
		{ fetchPolicy: "network-only" },
	);

	const isMeetingRecording = data.node?.__typename === "MeetingRecording";

	const recording = isMeetingRecording ? data.node : null;

	if (!isMeetingRecording || !recording) {
		return <TranscriptPreviewFailedModal closeModal={close} />;
	}

	return (
		<TranscriptModalContextProvider timestampStart={timestampStart} timestampEnd={timestampEnd}>
			<MeetingModalContextProvider noteId={data.node.note?.id}>
				<MeetingModal
					closeModal={close}
					viewer={data.viewer}
					note={data.node.note}
					recordings={[recording]}
					initialMeetingRecordingId={recording.id}
					initialVideoTimestamp={initialVideoTimestamp}
				/>
			</MeetingModalContextProvider>
		</TranscriptModalContextProvider>
	);
}

export default withSuspense(<LoadingMeetingModal />, MeetingModalThroughRecordingId);
